/**
 * Single source of truth for frontend routes.
 * Strips legacy "/app" segments so URLs stay at the site root.
 */
const stripLegacyApp = (value = '') =>
  String(value).replace(/\/app(?=\/|$)/g, '').replace(/\/+/g, '/');

const normalizeBase = () => {
  const raw = process.env.PUBLIC_URL || '';
  const cleaned = stripLegacyApp(raw).replace(/\/$/, '');
  return cleaned === '/' ? '' : cleaned;
};

export const APP_BASE = normalizeBase();

/** Remove legacy /app from a browser pathname (redirects, active menu checks). */
export const stripLegacyAppFromPath = (pathname = '') =>
  stripLegacyApp(pathname) || '/';

/** Remove theme layout suffix, e.g. /income/income-list/default/Dubai → /income/income-list */
export const stripThemeLayoutSuffix = (pathname = '') => {
  const cleaned = stripLegacyAppFromPath(pathname);
  const stripped = cleaned.replace(/\/default\/[^/]+$/, '');
  return stripped || '/';
};

const normalizePathForCompare = (pathname = '') => {
  const cleaned = stripLegacyAppFromPath(pathname).replace(/\/$/, '');
  return cleaned || '/';
};

/** True when current URL matches a menu route (exact or nested, e.g. /contributors/5). */
export const isActiveMenuPath = (currentPath = '', menuPath = '') => {
  if (!menuPath) return false;
  const current = normalizePathForCompare(currentPath);
  const menu = normalizePathForCompare(menuPath);
  if (current === menu) return true;
  return menu !== '/' && current.startsWith(`${menu}/`);
};

/** Public assets under /public (sprites, service worker, etc.). */
export const staticAsset = (pathname = '/') => {
  const raw = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return `${APP_BASE}${raw}`;
};

/** Relative path for nested <Route> children under path="/", e.g. '/income/income-list' → 'income/income-list' */
export const toRoutePath = (pathname = '/') => {
  const raw = pathname.startsWith('/') ? pathname.slice(1) : pathname;
  return stripLegacyApp(raw);
};

/** Build an app route, e.g. appPath('/contributors') => '/contributors' */
export const appPath = (pathname = '/') => {
  const raw = pathname === '' ? '/' : pathname;
  const normalized = stripLegacyApp(raw.startsWith('/') ? raw : `/${raw}`);
  const path = normalized || '/';
  return `${APP_BASE}${path}`;
};

export const APP_PATHS = {
  home: appPath('/'),
  dashboard: appPath('/dashboard'),
  login: appPath('/login'),
  loginOtp: (email) =>
    appPath(`/login/otp-verif/${encodeURIComponent(email)}`),
  forgetPwd: appPath('/forget-pwd'),
  resetPwd: appPath('/reset-pwd'),
  contributors: appPath('/contributors'),
  contributor: (id) => appPath(`/contributors/${id}`),
  users: appPath('/users/users-list'),
  artists: appPath('/artists/artists-list'),
  labels: appPath('/labels/labels-list'),
  userAccount: (layout) => appPath(`/users/account/${layout}`),
  income: appPath('/income/income-list'),
  incomeNew: appPath('/income/income-new'),
  incomeReport: (id) => appPath(`/income/report/${id}`),
  incomeYoutube: (type, id) => appPath(`/income/youtube/${type}/${id}`),
  exchange: appPath('/exchange'),
  products: appPath('/products/products-list'),
  productAssets: (productId) => appPath(`/products/${productId}/assets`),
  assetContributors: (productId, assetId) =>
    appPath(`/products/${productId}/assets/${assetId}/contributors`),
  invoices: appPath('/invoices/invoices-list'),
  expenses: appPath('/expenses'),
  expensesInhouse: appPath('/expenses/inhouse'),
  expensesRed: appPath('/expenses/red-expenses'),
  expensesContributors: appPath('/expenses/contributors-expenses'),
  invoicesNew: appPath('/invoices/invoices-new'),
  validation: appPath('/validation'),
  afr: appPath('/afr'),
  catalogSyncIssues: appPath('/catalog-sync-issues'),
  reportingSettings: appPath('/settings/reporting'),
  generalLedger: appPath('/general-ledger'),
  expensesManager: appPath('/expenses-manager'),
};

/** Redirect target after login — honour deep-link the user tried before auth. */
export const getPostLoginPath = (location, fallback = APP_PATHS.dashboard) => {
  const from = location?.state?.from;
  const fromPath = from?.pathname;
  if (fromPath && !fromPath.startsWith('/login')) {
    return `${fromPath}${from.search || ''}${from.hash || ''}`;
  }
  return fallback;
};

/**
 * Pathname for menu/active checks. Works with HashRouter (#/login)
 * and BrowserRouter (/login).
 */
export const getBrowserPathname = () => {
  if (typeof window === 'undefined') return '/';
  const { pathname, hash } = window.location;
  if (hash.startsWith('#/')) {
    return hash.slice(1).split('?')[0] || '/';
  }
  return pathname || '/';
};

/** Full URL for hard navigations (axios 401, etc.) under HashRouter. */
export const hashHref = (pathname = '/') => {
  const path = appPath(pathname);
  return `/#${path.startsWith('/') ? path : `/${path}`}`;
};

export default appPath;
